import { useRef, useState } from "react";
import { Plus, X } from "lucide-react";

const EMOJIS = ["✨", "🥰", "😎", "🤗", "😇", "🥳", "😬", "😳", "🫠", "😮‍💨", "🤯", "😵‍💫", "🥹", "😭", "🙃", "🤍", "🔥", "🌧️", "🌱", "⚡", "🫶", "💭", "🌊", "🎈"];

const CustomChip = ({ e, on, onToggle, onRemove }) => {
  const timer = useRef(null);
  const hold = () => { timer.current = setTimeout(() => { timer.current = "fired"; if (window.confirm(`Remove "${e.label}"?`)) onRemove(e.id); }, 600); };
  const release = () => { if (timer.current !== "fired") clearTimeout(timer.current); };
  const click = () => { if (timer.current === "fired") { timer.current = null; return; } onToggle(); };
  return (
    <span className={`mm-chip !pr-1.5 inline-flex items-center gap-1 ${on ? "mm-chip-on" : ""}`} data-testid={`custom-emotion-${e.id}`}>
      <button type="button" onClick={click} onPointerDown={hold} onPointerUp={release} onPointerLeave={release} onContextMenu={(ev) => ev.preventDefault()}
        className="select-none" data-testid={`custom-emotion-toggle-${e.id}`}>{e.emoji} {e.label}</button>
      <button type="button" onClick={() => onRemove(e.id)} className="p-0.5 rounded-full opacity-60 hover:opacity-100 hover:bg-black/10 transition-opacity" aria-label={`Remove ${e.label}`} data-testid={`custom-emotion-remove-${e.id}`}><X size={12} /></button>
    </span>
  );
};

const AddForm = ({ onAdd, onClose }) => {
  const [emoji, setEmoji] = useState(EMOJIS[0]);
  const [label, setLabel] = useState("");
  const submit = async (ev) => {
    ev.preventDefault();
    if (!label.trim()) return;
    if (await onAdd(emoji, label.trim())) onClose();
  };
  return (
    <form onSubmit={submit} className="mm-soft p-4 w-full mm-fade-up" data-testid="custom-emotion-form">
      <div className="flex flex-wrap gap-1.5" data-testid="custom-emotion-emoji-grid">
        {EMOJIS.map((x, i) => (
          <button type="button" key={x} onClick={() => setEmoji(x)} className={`w-9 h-9 rounded-xl text-lg transition-transform hover:scale-110 ${emoji === x ? "bg-mm-surface ring-2 ring-mm-primary" : ""}`} data-testid={`custom-emotion-emoji-${i}`}>{x}</button>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <span className="w-11 h-11 rounded-xl bg-mm-surface flex items-center justify-center text-xl shrink-0">{emoji}</span>
        <input autoFocus maxLength={24} className="mm-input !py-2" placeholder="Name your feeling…" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="custom-emotion-label-input" />
        <button className="mm-btn !px-4 !py-2" disabled={!label.trim()} data-testid="custom-emotion-save">Add</button>
        <button type="button" onClick={onClose} className="mm-btn-ghost !px-3 !py-2" aria-label="Cancel" data-testid="custom-emotion-cancel"><X size={16} /></button>
      </div>
    </form>
  );
};

/** Renders the user's saved emotions + an "Add your own" chip. `isOn(e)` / `onToggle(e)` work on {emoji,label}. */
export const CustomEmotions = ({ emotions, isOn, onToggle, onAdd, onRemove }) => {
  const [open, setOpen] = useState(false);
  const add = async (emoji, label) => {
    const e = await onAdd(emoji, label);
    if (e) onToggle(e);
    return e;
  };
  return (
    <>
      {emotions.map((e) => <CustomChip key={e.id} e={e} on={isOn(e)} onToggle={() => onToggle(e)} onRemove={onRemove} />)}
      {!open && (
        <button type="button" onClick={() => setOpen(true)} className="mm-chip border-dashed inline-flex items-center gap-1 text-mm-primary" data-testid="custom-emotion-add">
          <Plus size={14} /> Add your own
        </button>
      )}
      {open && <AddForm onAdd={add} onClose={() => setOpen(false)} />}
    </>
  );
};
